import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import { PieChart, Pie, Cell, Legend, Tooltip } from 'recharts';
import SkeletonLoader from '../../skeletonLoaders/Loader';
import CustomLegend from '../legend/CustomLegend';
import WidgetHeader from './common/WidgetHeader';
import { WidgetWrapper } from './common/styles';
import { getReports } from 'lib/api';
import { useQuery } from 'react-query';
import { widget } from './keys';
import {
  IParams,
  REPORTS_STALE_TIME,
  WidgetLabels,
  WidgetValues,
} from './types';
import { EmptyWidget } from '../EmptyWidget';
import CustomTooltip from '../tooltip/CustomTooltip';
import { useTranslation } from 'react-i18next';

const StatsWrapper = styled.div`
  display: flex;
  gap: 16px;
`;
const RecordedWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;
const RecordedNumber = styled.p`
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.black[100]};
`;
const RecordedDesc = styled.p`
  color: ${({ theme }) => theme.colors.black[60]};
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
`;

const COLORS = ['#001B53', '#B6E0EC', '#FF8B22'];

interface PieChartDescProps {
  title: string;
  params: IParams;
  addDataForExport: (data: any) => void;
}

interface IApiData {
  id: number | string;
  name: string;
  value: number;
}

interface IMapperData {
  data: IApiData[];
  total: number;
}

const apiUrl = 'metrics/videos/delivery-method';

const DeliveryPieChart = ({
  title,
  params,
  addDataForExport,
}: PieChartDescProps) => {
  const { t } = useTranslation();
  const { data, isLoading, isRefetching } = useQuery(
    widget.detail(WidgetValues.DELIVERY_METHOD, params),
    () => getReports(params, apiUrl),
    {
      staleTime: REPORTS_STALE_TIME,
    }
  );

  const mapDataForExport = (data: any) => {
    const { widgetData } = data;
    let exportData: any = {
      title: WidgetLabels.DELIVERY_METHOD,
      data: [],
      arr: [],
    };
    if (widgetData && widgetData.total !== 0) {
      let temp: any = {};
      widgetData?.data?.forEach((el: any) => {
        temp[el.name] = el.value;
      });
      temp[t('Total')] = widgetData.total;
      exportData.data.push(temp);
    }
    return exportData;
  };
  const organizeWidgetData = (data: any) => {
    let mapper: IMapperData = { data: [], total: 0 };
    let total = 0;
    mapper.data.push({
      id: 'Emails',
      name: t('Emails'),
      value: parseFloat(data?.emails.total),
    });
    if (!isNaN(parseFloat(data?.emails.total))) {
      total += parseFloat(data?.emails.total);
    }
    mapper.data.push({
      id: 'SMS',
      name: t('SMS'),
      value: parseFloat(data?.sms.total),
    });
    if (!isNaN(parseFloat(data?.sms.total))) {
      total += parseFloat(data?.sms.total);
    }
    mapper.data.push({
      id: 'Other',
      name: t('Other'),
      value: parseFloat(data?.other.total),
    });
    if (!isNaN(parseFloat(data?.other.total))) {
      total += parseFloat(data?.other.total);
    }
    mapper.data.sort(function (a, b) {
      return b.value - a.value;
    });
    mapper.total = total;
    return mapper;
  };

  let widgetData = organizeWidgetData(data);

  useEffect(() => {
    if (widgetData) {
      addDataForExport(mapDataForExport({ widgetData }));
    }
  }, [data]);

  if (isLoading) {
    return <SkeletonLoader imageName='pie-desc' />;
  }

  return (
    <WidgetWrapper>
      <WidgetHeader title={t(title)} isRefetching={isRefetching}>
        <StatsWrapper>
          {widgetData.total !== 0 && (
            <RecordedWrapper>
              <RecordedNumber>{widgetData.total}</RecordedNumber>
              <RecordedDesc>{t('sent')}</RecordedDesc>
            </RecordedWrapper>
          )}
        </StatsWrapper>
      </WidgetHeader>
      {widgetData.total !== 0 ? (
        <PieChart width={400} height={185}>
          <Pie
            data={widgetData.data}
            dataKey='value'
            fill='#FC0'
            innerRadius={30}
            cx={65}
            cy={75}
          >
            {widgetData.data.map((entry: any, index: number) => (
              <Cell key={entry.id} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Legend
            content={
              <CustomLegend
                showTooltip
                data={widgetData.data.map((entry, index) => ({
                  ...entry,
                  color: COLORS[index % COLORS.length],
                }))}
                total={widgetData.total}
              />
            }
            layout='vertical'
            verticalAlign='middle'
            align='right'
          />
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      ) : (
        <EmptyWidget />
      )}
    </WidgetWrapper>
  );
};

export default DeliveryPieChart;
